import { useLocalStorage } from '@shared/utils/useStorage';
import { useEffect, useRef } from 'react';
import { useUpdateLocalizationSetting } from '../userPreference/mutations';
import { useLocalizationSettingsQuery } from '../userPreference/queries';
import { languages } from './config';

export const useStoredLanguage = (defaultLanguage: string) => {
  const [language, setLanguage] = useLocalStorage<string>(
    'settings.language',
    defaultLanguage,
    { deserialize: (data) => data.replace('_', '-') }
  );

  const { data, isError, isSuccess } = useLocalizationSettingsQuery();
  const { mutateAsync: updateApiLanguage } =
    useUpdateLocalizationSetting('languagetag');
  const languageTag = data?.languageTag;
  const isValid = languages.some((l) => l.id === languageTag);
  const isReady = isSuccess || isError; // The api responds with 404 if no settings exists.
  const pendingLanguageRef = useRef<string | null>(null);

  useEffect(() => {
    if (languageTag && isValid) {
      if (
        pendingLanguageRef.current &&
        pendingLanguageRef.current !== languageTag
      ) {
        // Skip applying stale server values while a newer selection is pending.
        return;
      }

      pendingLanguageRef.current = null;

      if (language !== languageTag) {
        // Use existing stored languagetag from api.
        setLanguage(languageTag);
      }
    }
  }, [isValid, languageTag, language, setLanguage]);

  const updateLanguage = (value: string) => {
    pendingLanguageRef.current = value;
    setLanguage(value);
    updateApiLanguage(value).catch(() => {
      pendingLanguageRef.current = null;
    });
  };

  return { language, updateLanguage, isReady };
};
